import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CButton, CCard, CCardBody, CCol, CImage, CRow, CSpinner, CBadge } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faClose, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { review as reviewTranslations } from "@/constants/translation/review.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { review as reviewRoutes } from "@/libs/routes";
import { useGetReview, useDeleteReview } from "@/hooks/useReview";
import { dateTimeFormat } from "@/utils/dateFormate";
import avatar from "@/assets/images/avatars/DefaultImg.png";

const reviewT = reviewTranslations.view;

const ViewReview = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    data: response,
    isLoading,
    error: reviewError,
  } = useGetReview(id, null, (error) => {
    console.error("Error fetching review details:", error);
  });

  const { mutate: doDeleteReview } = useDeleteReview(() => {
    toast.success(t(reviewTranslations.list.messages.delete_success));
    navigate(reviewRoutes.list);
  });

  const reviewData = response?.data;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (reviewError || !reviewData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(reviewT.error.not_found)}</h4>
          <p>{t(reviewT.error.not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(reviewRoutes.list)}>
            {t(reviewT.back_to_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const na = t(reviewT.defaults.na);
  const { reviewer, freelancer, rating, comment, reviewDate, reply } = reviewData;

  const handleDelete = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(reviewTranslations.list.delete_popup.heading)}</h2>
            <p className="text-center mb-2">{t(reviewTranslations.list.delete_popup.message)}</p>
            <p className="text-center mb-3">{t(reviewTranslations.list.delete_popup.message_2)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteReview(reviewData.uuid);
                  onClose();
                }}
              >
                {t(reviewTranslations.list.delete_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(reviewTranslations.list.delete_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  return (
    <div className="view-review-container">
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md="auto" className="text-center mb-3 mb-md-0">
              <CImage
                src={reviewer?.profilePhotoUrl || avatar}
                alt="reviewer_img"
                className="rounded-circle"
                style={{ width: "90px", height: "90px", objectFit: "cover", border: "4px solid #212631" }}
              />
            </CCol>
            <CCol>
              <h2 className="mb-1">{reviewer?.fullName || na}</h2>
              <div className="text-muted mb-2">{t(reviewT.freelancer_section.title)}: {freelancer?.name || na}</div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <CBadge color="warning" className="text-dark d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faStar} />
                  {rating ?? na}
                </CBadge>
                <span className="text-muted small">{dateTimeFormat(reviewDate) || na}</span>
              </div>
              <CButton size="sm" color="danger" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} className="me-1" />
                {t(reviewT.actions.delete)}
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CRow>
        <CCol md={6} className="mb-4">
          <CCard className="h-100">
            <CCardBody>
              <h4 className="card-title mb-4">{t(reviewT.reviewer_section.title)}</h4>
              <div className="mb-3">
                <strong>{t(reviewT.reviewer_section.name_label)}:</strong>
                <p className="text-muted">{reviewer?.fullName || na}</p>
              </div>
              <div className="mb-3">
                <strong>{t(reviewT.reviewer_section.username_label)}:</strong>
                <p className="text-muted">{reviewer?.userName || na}</p>
              </div>
              <div className="mb-3">
                <strong>{t(reviewT.reviewer_section.email_label)}:</strong>
                <p className="text-muted">{reviewer?.email || na}</p>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6} className="mb-4">
          <CCard className="h-100">
            <CCardBody>
              <h4 className="card-title mb-4">{t(reviewT.freelancer_section.title)}</h4>
              <div className="mb-3">
                <strong>{t(reviewT.freelancer_section.business_name_label)}:</strong>
                <p className="text-muted">{freelancer?.name || na}</p>
              </div>
              <div className="mb-3">
                <strong>{t(reviewT.freelancer_section.account_holder_label)}:</strong>
                <p className="text-muted">{freelancer?.accountHolderName || na}</p>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="mb-4">
        <CCardBody>
          <h4 className="card-title mb-4">{t(reviewT.review_section.title)}</h4>
          <CRow>
            <CCol md={6} className="mb-3">
              <strong>{t(reviewT.review_section.rating_label)}:</strong>
              <p className="text-muted d-flex align-items-center gap-1">
                <FontAwesomeIcon icon={faStar} className="text-warning" />
                {rating ?? na}
              </p>
            </CCol>
            <CCol md={6} className="mb-3">
              <strong>{t(reviewT.review_section.review_date_label)}:</strong>
              <p className="text-muted">{dateTimeFormat(reviewDate) || na}</p>
            </CCol>
            <CCol md={12} className="mb-3">
              <strong>{t(reviewT.review_section.comment_label)}:</strong>
              <p className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{comment || na}</p>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardBody>
          <h4 className="card-title mb-4">{t(reviewT.reply_section.title)}</h4>
          {reply ? (
            <>
              <p className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{reply.replyText}</p>
              <div className="text-muted small">
                {t(reviewT.reply_section.replied_on_label)}: {dateTimeFormat(reply.createdAt) || na}
              </div>
            </>
          ) : (
            <p className="text-muted">{t(reviewT.reply_section.no_reply)}</p>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
};

ViewReview.breadCrumbArray = [
  {
    name: breadcrumb_key.review?.list,
    pathname: reviewRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.review?.view,
    pathname: "",
    active: true,
  },
];

export default ViewReview;
